// src/components/Tours.jsx
import React from "react";
import { Helmet } from "react-helmet";
import "./Tours.css";
import { Link } from "react-router-dom";
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import { Trans, useTranslation } from "react-i18next";
import { FiDownload } from "react-icons/fi";
import useScrollToTop from "./ScrollToTop";

const Tours = () => {
  useScrollToTop();
  const { t } = useTranslation("tours");

  // --- Mapping for tour images and links ---
  const tourResources = [
    { image: "/images/brett-jordan-vjSY59Td6y4-unsplash.jpg", link: "/Bush-BeachSafari" },
    { image: "/images/treat.jpeg", link: "/Osiligi-Clinic-Safari" },
    { image: "/images/_12A1727.jpg", link: "/Mara-DeepDive" },
    { image: "/images/HIPPOS.jpg", link: "/RiftValleyClassic" },
    { image: "/images/elephant1.jpg", link: "/Amboseli+Tsavo" },
    { image: "/images/ZEBRA-CROSSING.jpg", link: "/Conservation-Safari" },
    { image: "/images/marariver.jpg", link: "/CrossBorderSafari" },
    { image: "/images/cheetcub.jpg", link: "/Kenya-Classic" }
  ];

  // --- Accommodation images mapped directly in component ---
  const accommodationData = [
    {
      id: "naivasha",
      image: "/images/panorama.jpg", // Direct image path in component
      title: t("accommodation.naivasha.title"),
      description: t("accommodation.naivasha.description"),
      link: "/accomodations#naivasha"
    },
    {
      id: "mara", 
      image: "/images/oldarpoi_room1.jpg", // Direct image path in component
      title: t("accommodation.mara.title"),
      description: t("accommodation.mara.description"),
      link: "/accomodations#mara"
    },
    {
      id: "narok",
      image: "/images/breakfast.jpg", // Direct image path in component
      title: t("accommodation.narok.title"),
      description: t("accommodation.narok.description"),
      link: "/accomodations#narok"
    }
  ];

  const toursArray = t("tours", { returnObjects: true });

  return (
    <div className="tours-container">

      {/* --- SEO --- */}
      <Helmet>
        <title>Kenya Safari Packages | Budget to Luxury Tours - Olenini Safaris</title>
        <meta name="description" content="Explore our Kenya safari packages including Maasai Mara, Amboseli, Lake Naivasha, and more. Options available for families, honeymooners, and photographers. Book your adventure with Olenini Safaris." />
        <meta name="keywords" content="Kenya safari packages, budget safaris Kenya, luxury safari Kenya, family safari Kenya, safari from Nairobi, African tours packages" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://oleninisafaris.com/tours" />

        {/* Open Graph */}
        <meta property="og:title" content="Kenya Safari Packages | Olenini Safaris" />
        <meta property="og:description" content="Choose from our safari packages to top destinations in Kenya — Maasai Mara, Amboseli, Nakuru, and more. From budget to luxury." />
        <meta property="og:image" content="https://oleninisafaris.com/images/CALTURAL.jpg" />
        <meta property="og:url" content="https://oleninisafaris.com/tours" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kenya Safari Packages | Olenini Safaris" />
        <meta name="twitter:description" content="Explore our range of Kenya safari packages – designed for all budgets and interests. Maasai Mara, Amboseli, Naivasha and more." />
        <meta name="twitter:image" content="https://oleninisafaris.com/images/CALTURAL.jpg" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Kenya Safari Packages",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Maasai Mara Safari Package",
                "url": "https://oleninisafaris.com/packagemara"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Lake Nakuru Safari Package",
                "url": "https://oleninisafaris.com/packagenakuru"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Family Safari Package",
                "url": "https://oleninisafaris.com/packagefamily"
              },
              {
                "@type": "ListItem",
                "position": 4,
                "name": "Osiligi Safari Package",
                "url": "https://oleninisafaris.com/packageosiligi"
              }
            ]
          }
          `}
        </script>
      </Helmet>

      <div className="tour-highlight-card">
        <div className="tour-text-container">
          <h2>{t('title', { ns: 'tours' })}</h2>
          <p>
            <Trans
              i18nKey="paragraph"
              ns="tours"
              components={{ 1: <b /> }}
            />
          </p>
        </div>
      </div>

      {/* --- Tours Section --- */}
      <div className="tours-section">
        <div className="premium-tours-grid">
          {toursArray.map((tour, index) => {
            const resource = tourResources[index] || {};
            return (
              <div key={index} className="tour-card-pro">
                <div className="card-image-container">
                  <img src={resource.image} alt={tour.alt || tour.title} />
                </div>
                <div className="card-content">
                  <h3>{tour.title}</h3>
                  <p className="card-excerpt">{tour.excerpt}</p>
                  <div className="card-features">
                    <span className="price">{tour.price}</span>
                    <Link to={resource.link} className="card-cta">
                      {tour.cta}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* --- Video Section --- */}
        <div className="savanna-showcase-video">
          <video 
            src="/videos/maasai mara 12.mp4"
            autoPlay 
            loop 
            muted 
            playsInline 
            aria-label="Wildlife experience in Maasai Mara"
          />
        </div>

        {/* --- Overview --- */}
        <div className="kenyaSafariOverview-card">
          <p>
            <span dangerouslySetInnerHTML={{ __html: t("overview.intro_1") }} />{" "}
            {t("overview.intro_2")} <br /><br />
            {t("overview.intro_3")} <br /><br />
            {t("overview.intro_4")} <br />
            {t("overview.intro_5")} <br /><br />
            {t("overview.cta_1")}
          </p>
          <hr style={{ border: "1.5px solid #ccc", margin: "15px 0" }} />

          <div className="custom">
            <p>{t("overview.customize_intro")}</p>
          </div>

          <div className="customize-button-wrapper">
            <Link to="/customizesafari" className="safaricust-button" aria-label={t("overview.button_label")}>
              {t("overview.button_label")}
            </Link>
          </div>
          <hr style={{ border: "2px solid #ccc", margin: "15px 0" }} />
        </div>

        {/* --- Foundation Section --- */}
        <div className="foundation-highlight-card">
          <div className="foundation-slider-section">
            <Slider
              autoplay={true}
              autoplaySpeed={3000}
              infinite={true}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              dots={false}
              pauseOnHover={false}
              swipeToSlide={true}
            >
              <div><img src="/images/treat.jpeg" alt="Slide 1" className="foundation-slide-image" /></div>
              <div><img src="/images/IMG-20250703-WA0013 - Copy.jpg" alt="Slide 2" className="foundation-slide-image" /></div>
              <div><img src="/images/intro.jpeg" alt="Slide 3" className="foundation-slide-image" /></div>
              <div><img src="/images/clinic.jpeg" alt="Slide 4" className="foundation-slide-image" /></div>
            </Slider>
          </div>

          <div className="foundation-text-section">
            <h2>{t("foundation.title")}</h2>
            <strong>{t("foundation.subtitle")}</strong>
            <p dangerouslySetInnerHTML={{ __html: t("foundation.description") }} />
            <Link to="/Osiligi" className="foundation-learn-more">
              {t("foundation.button")}
            </Link>
          </div>
        </div>

        <hr />

        {/* --- Accommodations Section --- */}
        <div className="accomodation-section">
          <h3>{t("accommodation.title")}</h3>
          <div className="accomodation-grid">
            {accommodationData.map((accommodation) => (
              <div key={accommodation.id} className="accomodation-card">
                <div className="image-hover-container">
                  <img 
                    src={accommodation.image} 
                    alt={accommodation.title} 
                    loading="lazy" 
                  />
                  <div className="static-text">
                    <h4>{accommodation.title}</h4>
                  </div>
                  <div className="overlay">
                    <div className="overlay-content">
                      <p>{accommodation.description}</p>
                      <a href={accommodation.link} className="overlay-btn">
                        {t("accommodation.view_more")}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* --- Dining Section --- */}
        <section className="dinning-section">
          <h3>{t("dining.title")}</h3>
          <p>{t("dining.description")}</p>
          <br />
          <div className="amenities-container">
            <div className="textAmenities-card">
              <h4>{t("dining.amenities.title")}</h4>
              <ul className="dinning-amenities">
                {["open_air","experiences","cuisine","wifi","power"].map(key => (
                  <li key={key} dangerouslySetInnerHTML={{ __html: t(`dining.amenities.${key}`) }} />
                ))}
              </ul>
              <div className="dl-container">
                <a 
                  href="/Olenini-safaris.pptx" 
                  download="Olenini-Safaris.pptx" 
                  className="dl-button"
                  style={{ color: 'white' }}
                >
                  <FiDownload size={18} />
                  Download Brochure
                </a>
              </div>
            </div>
            <div className="imgAmenities-card">
              <Slider
                autoplay={true}
                autoplaySpeed={2500}
                infinite={true}
                slidesToShow={1}
                slidesToScroll={1}
                arrows={false}
                dots={false}
                pauseOnHover={true}
                swipeToSlide={true}
                adaptiveHeight={false}
              >
                {["breakfast.jpg","serianbreakfast.jpeg","forestdining.jpeg"].map((img, idx) => (
                  <div key={idx}>
                    <img src={`/images/${img}`} alt="Dining Experience" className="slider-image" />
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Tours;